import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  TextField,
  makeStyles
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { isLikelyLidNumber, safeFormatPhoneNumber } from "../../utils/formatSerializedId";

const useStyles = makeStyles((theme) => {
  const isLight = theme.mode === "light";

  return {
    panel: {
      margin: theme.spacing(1, 0),
      padding: theme.spacing(1.5),
      borderRadius: 12,
      backgroundColor: isLight ? "#fffbeb" : "#422006",
      border: `1px solid ${isLight ? "#f59e0b" : "#d97706"}`,
      boxShadow: isLight
        ? "0 1px 3px rgba(245, 158, 11, 0.12)"
        : "0 2px 8px rgba(0, 0, 0, 0.24)",
    },
    title: {
      color: isLight ? "#92400e" : "#fde68a",
      fontWeight: 700,
    },
    description: {
      marginTop: theme.spacing(0.75),
      color: isLight ? "#78350f" : "#fcd34d",
      lineHeight: 1.45,
    },
    caption: {
      marginTop: theme.spacing(1),
      color: isLight ? "#92400e" : "#fde68a",
    },
    autocomplete: {
      marginTop: theme.spacing(1.25),
    },
    textField: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: isLight ? "#ffffff" : "#1e293b",
        borderRadius: 10,
        "& fieldset": {
          borderColor: isLight ? "rgba(146, 64, 14, 0.35)" : "rgba(251, 191, 36, 0.35)",
        },
        "&:hover fieldset": {
          borderColor: isLight ? "#f59e0b" : "#fbbf24",
        },
        "&.Mui-focused fieldset": {
          borderColor: isLight ? "#d97706" : "#fcd34d",
        },
      },
      "& .MuiOutlinedInput-input": {
        color: isLight ? "#0f172a" : "#f8fafc",
      },
      "& .MuiInputLabel-root": {
        color: isLight ? "#92400e" : "#fde68a",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: isLight ? "#d97706" : "#fcd34d",
      },
      "& .MuiFormHelperText-root": {
        color: isLight ? "#78350f" : "#fcd34d",
      },
      "& .MuiSvgIcon-root": {
        color: isLight ? "#92400e" : "#fde68a",
      },
    },
    mergeButton: {
      marginTop: theme.spacing(1.25),
      borderRadius: 10,
      fontWeight: 600,
    },
    loader: {
      marginTop: theme.spacing(1.5),
      color: isLight ? "#d97706" : "#fbbf24",
    },
    listbox: {
      backgroundColor: isLight ? "#ffffff" : "#1e293b",
      color: isLight ? "#0f172a" : "#f1f5f9",
      border: isLight
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(148, 163, 184, 0.2)",
      "& .MuiAutocomplete-option": {
        "&[data-focus=true]": {
          backgroundColor: isLight ? "rgba(245, 158, 11, 0.12)" : "rgba(251, 191, 36, 0.16)",
        },
        '&[aria-selected="true"]': {
          backgroundColor: isLight ? "rgba(245, 158, 11, 0.2)" : "rgba(251, 191, 36, 0.24)",
        },
      },
    },
    noOptions: {
      color: isLight ? "#64748b" : "#cbd5e1",
    },
  };
});

const LidMergePanel = ({ contact, onMerged }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [data, setData] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [agendaOptions, setAgendaOptions] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);

  const isUnresolved =
    contact?.remoteJid?.includes("@lid") &&
    isLikelyLidNumber(String(contact?.number || "").replace(/\D/g, ""))
      ? false
      : isLikelyLidNumber(String(contact?.number || "").replace(/\D/g, "")) ||
        String(contact?.number || "").startsWith("lid:");

  useEffect(() => {
    if (!contact?.id || !isUnresolved) {
      setLoading(false);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const { data: response } = await api.get(
          `/contacts/lid/suggestions/${contact.id}`
        );
        setData(response);

        if (response.autoSuggestion) {
          setSelectedContact(response.autoSuggestion);
          setAgendaOptions([response.autoSuggestion]);
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [contact?.id, isUnresolved]);

  useEffect(() => {
    if (!contact?.id || !isUnresolved) return;

    const term = searchParam.trim();
    if (term.length < 2) {
      if (data?.autoSuggestion) {
        setAgendaOptions([data.autoSuggestion]);
      } else {
        setAgendaOptions([]);
      }
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data: results } = await api.get("/contacts/lid/agenda-search", {
          params: {
            search: term,
            excludeContactId: contact.id
          }
        });
        setAgendaOptions(results || []);
      } catch (err) {
        toastError(err);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchParam, contact?.id, isUnresolved, data?.autoSuggestion]);

  if (!isUnresolved) return null;

  const handleMerge = async () => {
    if (!selectedContact?.id) {
      toast.error("Selecciona un contacto de la agenda para fusionar");
      return;
    }

    try {
      setMerging(true);
      await api.post("/contacts/lid/merge", {
        lidContactId: contact.id,
        canonicalContactId: selectedContact.id
      });
      toast.success("Contacto asociado correctamente");
      if (onMerged) onMerged();
    } catch (err) {
      toastError(err);
    } finally {
      setMerging(false);
    }
  };

  const getOptionLabel = option => {
    if (!option) return "";
    const formatted = safeFormatPhoneNumber(option.number);
    return formatted ? `${option.name} — ${formatted}` : option.name;
  };

  return (
    <Paper square={false} variant="outlined" className={classes.panel}>
      <Typography variant="subtitle2" className={classes.title}>
        Contacto pendiente de resolver (LID)
      </Typography>
      <Typography variant="body2" className={classes.description}>
        WhatsApp envió este chat con ID privado. Busca en tu agenda el contacto
        real (569...) para asociarlo.
      </Typography>

      {loading ? (
        <CircularProgress size={22} className={classes.loader} />
      ) : (
        <>
          {data?.matchReason && data?.autoSuggestion && (
            <Typography variant="caption" display="block" className={classes.caption}>
              Sugerencia automática ({data.matchReason}):{" "}
              {data.autoSuggestion.name} —{" "}
              {safeFormatPhoneNumber(data.autoSuggestion.number)}
            </Typography>
          )}

          <Autocomplete
            fullWidth
            size="small"
            className={classes.autocomplete}
            options={agendaOptions}
            value={selectedContact}
            loading={searchLoading}
            onChange={(_e, newValue) => setSelectedContact(newValue)}
            getOptionLabel={getOptionLabel}
            getOptionSelected={(option, value) => option.id === value.id}
            classes={{ listbox: classes.listbox, noOptions: classes.noOptions }}
            noOptionsText={
              searchParam.trim().length < 2
                ? "Escribe al menos 2 letras para buscar en la agenda"
                : "Sin resultados"
            }
            renderInput={params => (
              <TextField
                {...params}
                className={classes.textField}
                variant="outlined"
                label="Buscar contacto de agenda"
                placeholder="Nombre o número 569..."
                onChange={e => setSearchParam(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? (
                        <CircularProgress className={classes.loader} size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />

          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={classes.mergeButton}
            disabled={merging || !selectedContact?.id}
            onClick={handleMerge}
          >
            {merging ? "Fusionando..." : "Asociar con contacto de agenda"}
          </Button>
        </>
      )}
    </Paper>
  );
};

export default LidMergePanel;
